/**
 * Observability for the global 401 interceptor's redirect decision (IPS-711).
 *
 * Goal: after release, confirm whether real users hit the route-scoped guard that
 * suppresses the login redirect on public/no-login pages, and whether the 401 is
 * coming from the expected background calls — and compare that against the normal
 * protected-route redirect.
 *
 * Sentry is not wired in web-fe yet; structured console messages are what gets
 * surfaced for now, so we emit a single greppable `auth_redirect_decision` payload.
 * We deliberately keep raw tokens, magic-link query params, and emails OUT of it:
 * the path/URL is sanitized (query + hash dropped, secret token segments redacted)
 * and token presence is reported only as booleans.
 */

export type AuthRedirectAction = 'redirect_suppressed' | 'redirect_to_login';
export type AuthRouteType = 'public_no_login' | 'protected';

export interface AuthRedirectDecision {
  action: AuthRedirectAction;
  routeType: AuthRouteType;
  currentUrl?: string; // raw; sanitized inside before logging
  failedRequestUrl?: string; // raw; sanitized inside before logging
  hadAccessToken: boolean;
  hadRefreshToken: boolean;
}

/**
 * Reduce a path or full URL to a safe-to-log form: drop the query string and hash
 * (where magic-link params live) and redact the secret token segment of the known
 * link routes, keeping enough of the path to locate the flow.
 */
export const sanitizeAuthUrl = (input?: string): string => {
  if (!input) return '';

  let path = input;
  try {
    // Absolute URL: keep only the pathname (drops origin, query and hash).
    path = new URL(input).pathname;
  } catch {
    // Relative path: drop query and hash manually.
    path = input.split('#')[0].split('?')[0];
  }

  return (
    path
      // FE route: /story/collection-link/:slug/:token[/...] -> redact :token
      .replace(/(\/story\/collection-link\/[^/]+\/)[^/]+/, '$1<redacted>')
      // API: /collection-link/story|response/:token -> redact the token/id
      .replace(/(\/collection-link\/(?:story|response)\/)[^/]+/, '$1<redacted>')
      // Register links: /register/:ref/:token[/:inviteId] -> redact token onwards
      .replace(/(\/register\/[^/]+\/)[^/]+(?:\/[^/]+)?/, '$1<redacted>')
  );
};

export const logAuthRedirectDecision = (decision: AuthRedirectDecision): void => {
  const payload = {
    event: 'auth_redirect_decision',
    action: decision.action,
    routeType: decision.routeType,
    path: sanitizeAuthUrl(decision.currentUrl),
    failedRequestUrl: sanitizeAuthUrl(decision.failedRequestUrl),
    hadAccessToken: decision.hadAccessToken,
    hadRefreshToken: decision.hadRefreshToken,
  };

  // Suppressed redirects are the thing we want to see (warning); the normal
  // protected-route redirect is logged at a lower level as comparison context.
  if (decision.action === 'redirect_suppressed') {
    console.warn('[auth_redirect_decision]', payload);
  } else {
    console.log('[auth_redirect_decision]', payload);
  }
};
